import { Popover } from '@headlessui/react'
import React, { useState } from 'react'
import { usePopper } from 'react-popper'
import {
  selectShowCustomLabels,
  selectShowDefinitions,
  selectShowRevisionHighlights,
  selectShowClauseReferences,
  setShowCustomLabels,
  setShowDefinitions,
  setShowRevisionHighlights,
  setShowClauseReferences,
} from '../../../redux/viewer-slice'
import { useDispatch, useSelector } from 'react-redux'
import { EyeIcon, EyeSlashIcon, LinkIcon } from '@heroicons/react/24/outline'
import { Switch } from '@mantine/core'
import { useGetUserProfileQuery } from '../../../redux/api-slice'

interface DocumentViewerViewOptionsProps {
  // Define your component props here
}

const DocumentViewerViewOptions: React.FC<DocumentViewerViewOptionsProps> = (
  props
) => {
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(
    null
  )
  const [popperElement, setPanelRef] = useState<HTMLElement | null>(null)
  const { styles, attributes } = usePopper(referenceElement, popperElement)
  const showRevisionHighlights = useSelector(selectShowRevisionHighlights)
  const showCustomLabels = useSelector(selectShowCustomLabels)
  const showDefinitions = useSelector(selectShowDefinitions)
  const showClauseReferences = useSelector(selectShowClauseReferences)
  const dispatch = useDispatch()
  const { data: user } = useGetUserProfileQuery(undefined)

  const onToggleShowRevisionHighlights = () => {
    dispatch(setShowRevisionHighlights(!showRevisionHighlights))
  }

  const onToggleShowCustomLabels = () => {
    dispatch(setShowCustomLabels(!showCustomLabels))
  }

  const onToggleShowDefinitions = () => {
    dispatch(setShowDefinitions(!showDefinitions))
  }

  const onToggleShowClauseReferences = () => {
    dispatch(setShowClauseReferences(!showClauseReferences))
  }

  return (
    <div className="flex items-center space-x-2 px-4">
      <Popover>
        <Popover.Button
          className={
            'group rounded-lg border-2 border-[#ffffff00] px-2 py-1.5 transition-colors hover:border-2 hover:border-[#e5e7eb] hover:!bg-[#fff]'
          }
          tabIndex={-1}
          ref={setReferenceElement}
        >
          <div className="flex items-center justify-center text-gray-500 group-hover:text-black">
            <EyeIcon className="mr-1 h-4 w-4 text-gray-500 group-hover:text-black" />
            Highlights
          </div>
        </Popover.Button>
        <Popover.Panel
          ref={setPanelRef}
          style={styles.popper}
          {...attributes.popper}
          className="z-50 flex flex-col rounded border bg-white shadow"
        >
          <button
            className="relative flex items-center gap-2 p-2 text-sm"
            onClick={onToggleShowRevisionHighlights}
          >
            {showRevisionHighlights ? (
              <EyeIcon className="h-4 w-4" />
            ) : (
              <EyeSlashIcon className="h-4 w-4" />
            )}
            {showRevisionHighlights ? 'Hide' : 'Show'} Comment & Revision
            Highlighting
          </button>

          <button
            className="relative flex items-center gap-2 p-2 text-sm"
            onClick={onToggleShowCustomLabels}
          >
            {showCustomLabels ? (
              <EyeIcon className="h-4 w-4" />
            ) : (
              <EyeSlashIcon className="h-4 w-4" />
            )}
            {showCustomLabels ? 'Hide' : 'Show'} Labels & Tags Highlighting
          </button>
          <button
            className="relative flex items-center gap-2 p-2 text-sm"
            onClick={onToggleShowDefinitions}
          >
            {showDefinitions ? (
              <EyeIcon className="h-4 w-4" />
            ) : (
              <EyeSlashIcon className="h-4 w-4" />
            )}
            {showDefinitions ? 'Hide' : 'Show'} Definition Highlighting
          </button>
          {user?.feature_flags?.section_reference ? (
            <button
              className="relative flex items-center gap-2 p-2 text-sm"
              onClick={onToggleShowClauseReferences}
            >
              {showClauseReferences ? (
                <EyeIcon className="h-4 w-4" />
              ) : (
                <EyeSlashIcon className="h-4 w-4" />
              )}
              {showClauseReferences ? 'Hide' : 'Show'} Section References
            </button>
          ) : null}
        </Popover.Panel>
      </Popover>
    </div>
  )
}

export default DocumentViewerViewOptions
